import { PublicAxiosInstance } from '@/api/axios'
import ConfirmModal from '@/components/modal/confirm-modal'
import {
  PolicyAcknowledgementContent,
  PolicyOwnerDetails,
  type IAcknowledgementPolicy,
} from '@/components/policy/policy-acknowledgement-content'
import { PolicyAiSummaryCard } from '@/components/policy/policy-ai-summary-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FullPageSkeleton, Skeleton } from '@/components/ui/skeleton'
import { useDateFormat } from '@/hooks/use-date-format'
import { usePolicyAiSummary } from '@/hooks/use-policy-ai-summary'
import { PolicyGenerationMethod } from '@/enum/global.enum'
import { cn } from '@/lib/utils'
import { policyStatusColor, policyStatusLabel } from '@/utils/policy-status-label.utils'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { ArrowLeft, Check, ChevronRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { AIButton } from '@/components/ui/ai-button'

interface ISharedPolicySummary {
  policyHistoryId: string
  policyId: string
  name: string
  status: string
  policyCategory: { name: string } | null
  isAcknowledged: boolean
  acknowledgementDate: string | null
}

interface IPoliciesBundleData {
  policies: ISharedPolicySummary[]
  staff: { name: string }
  accessInfo: {
    accessedAt: string
    expiresAt: string
  }
}

interface IPolicyDetailData {
  policy: IAcknowledgementPolicy
  accessInfo: {
    accessedAt: string
    expiresAt: string
    isAcknowledged: boolean
  }
}

type ApiError = AxiosError<{ message: string }>

type PolicyFilter = 'all' | 'pending' | 'acknowledged'

const SELECTED_POLICY_PARAM = 'policy'

const FILTERS: { value: PolicyFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'acknowledged', label: 'Acknowledged' },
]

const getErrorMessage = (err: unknown, fallback: string) =>
  (err as ApiError)?.response?.data?.message ?? (err instanceof Error ? err.message : fallback)

const hasWrittenContent = (policy?: IAcknowledgementPolicy) =>
  !!policy?.description?.trim() &&
  (policy.generationMethod === PolicyGenerationMethod.MANUAL || policy.generationMethod === PolicyGenerationMethod.AI)

const getAccessErrorDisplay = (statusCode: number | null) => {
  if (statusCode === 410)
    return {
      title: 'Link expired',
      message: 'This link has expired. Please contact your organisation admin to request a new link.',
    }

  if (statusCode === 401)
    return {
      title: 'Invalid link',
      message: 'This link is invalid or has expired. Please request a new link from your administrator.',
    }

  if (statusCode === 404)
    return {
      title: 'Not found',
      message: 'The policies you are looking for could not be found.',
    }

  return {
    title: 'Access error',
    message: 'Failed to load policies. Please try again later.',
  }
}

const AccessError = ({ statusCode }: { statusCode: number | null }) => {
  const { title, message } = getAccessErrorDisplay(statusCode)

  return (
    <div className='flex min-h-dvh items-center justify-center px-4'>
      <div className='w-full max-w-sm text-center'>
        <h2 className='text-lg font-semibold'>{title}</h2>
        <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>{message}</p>
        <Button className='mt-5' onClick={() => (window.location.href = '/')}>
          Go home
        </Button>
      </div>
    </div>
  )
}

const ProgressSummary = ({ acknowledged, total }: { acknowledged: number; total: number }) => {
  const progress = total ? Math.round((acknowledged / total) * 100) : 0

  return (
    <div className='w-full sm:w-52'>
      <div className='mb-1.5 flex items-center justify-between gap-3 text-xs text-muted-foreground'>
        <span>
          <span className='font-medium text-foreground'>{acknowledged}</span> of {total}
        </span>
        <span className='tabular-nums'>{progress}%</span>
      </div>
      <div
        className='h-1 w-full overflow-hidden rounded-full bg-muted'
        role='progressbar'
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label='Acknowledgement progress'
      >
        <div
          className='h-full rounded-full bg-foreground transition-[width] duration-500'
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}

const FilterTabs = ({
  value,
  counts,
  onChange,
}: {
  value: PolicyFilter
  counts: Record<PolicyFilter, number>
  onChange: (value: PolicyFilter) => void
}) => (
  <div
    role='tablist'
    aria-label='Filter policies'
    className='flex gap-5   overflow-x-auto border-b [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
  >
    {FILTERS.map((filter) => {
      const isActive = filter.value === value
      return (
        <button
          key={filter.value}
          type='button'
          role='tab'
          aria-selected={isActive}
          onClick={() => onChange(filter.value)}
          className={cn(
            '-mb-px cursor-pointer flex h-10 shrink-0 items-center gap-1.5 border-b-2 text-sm whitespace-nowrap',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isActive ? 'border-foreground font-medium text-foreground' : 'border-transparent text-muted-foreground'
          )}
        >
          {filter.label}
          <span className='tabular-nums'>{counts[filter.value]}</span>
        </button>
      )
    })}
  </div>
)

const PolicyListItem = ({
  policy,
  isSelected,
  onSelect,
}: {
  policy: ISharedPolicySummary
  isSelected: boolean
  onSelect: () => void
}) => {
  const { formatDate } = useDateFormat()
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isSelected) buttonRef.current?.scrollIntoView({ block: 'nearest' })
  }, [isSelected])

  const meta = [
    policy.policyCategory?.name,
    policy.isAcknowledged
      ? policy.acknowledgementDate
        ? formatDate(policy.acknowledgementDate)
        : 'Acknowledged'
      : 'Pending',
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <button
      ref={buttonRef}
      type='button'
      onClick={onSelect}
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-3 border-b px-2 py-3 text-left sm:px-2',
        'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isSelected && 'bg-muted/60'
      )}
    >
      <span
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          policy.isAcknowledged ? 'border-foreground bg-foreground text-background' : 'border-input text-transparent'
        )}
        aria-hidden
      >
        <Check className='h-3 w-3' />
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-sm font-medium'>{policy.name}</span>
        <span className='mt-0.5 block truncate text-xs text-muted-foreground'>{meta}</span>
      </span>
      <ChevronRight className='h-4 w-4 shrink-0 text-muted-foreground' />
    </button>
  )
}

const PolicyDetailPanel = ({
  token,
  summary,
  position,
  total,
  nextPendingPolicy,
  onClose,
  onAcknowledged,
  onSelectNext,
}: {
  token: string
  summary: ISharedPolicySummary
  position: number
  total: number
  nextPendingPolicy?: ISharedPolicySummary
  onClose: () => void
  onAcknowledged: (policyHistoryId: string) => void
  onSelectNext: (policyHistoryId: string) => void
}) => {
  const [openModal, setOpenModal] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const { formatDate } = useDateFormat()

  const { data, isLoading, error } = useQuery({
    queryKey: ['policies-bundle-detail', token, summary.policyHistoryId],
    queryFn: async () => {
      const response = await PublicAxiosInstance.get(`/policy/secure-bundle/${token}/${summary.policyHistoryId}`)
      return response.data.data as IPolicyDetailData
    },
  })

  const isAcknowledged = summary.isAcknowledged || !!data?.accessInfo?.isAcknowledged
  const [isSummaryOpen, setIsSummaryOpen] = useState(false)

  const aiSummary = usePolicyAiSummary({ token, policyHistoryId: summary.policyHistoryId })

  const handleSummarise = () => {
    setIsSummaryOpen(true)
    aiSummary.summarise()
  }

  const handleAcknowledgement = async () => {
    if (isAcknowledged) return
    try {
      await PublicAxiosInstance.post(`/policy/secure-bundle/${token}/${summary.policyHistoryId}/acknowledgement`)
      onAcknowledged(summary.policyHistoryId)
      toast.success('Policy acknowledged successfully')
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to acknowledge policy'))
    }
  }

  const policy = data?.policy
  const status = policy?.status ?? summary.status
  const policyName = policy?.name ?? summary.name

  return (
    <section className='flex h-full min-h-0 flex-col bg-background' aria-labelledby='policy-detail-title'>
      <div className='flex-shrink-0 border-b px-4 py-3 sm:px-6'>
        <div className='flex items-start justify-between gap-3'>
          <div className='min-w-0'>
            <Button variant='ghost' size='sm' className='-ml-2 mb-1 h-8 px-2 md:hidden' onClick={onClose}>
              <ArrowLeft className='h-4 w-4' />
              Policies
            </Button>
            <h2 id='policy-detail-title' className='text-lg font-semibold leading-snug break-words sm:text-xl'>
              {policyName}
            </h2>
            <div className='mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1'>
              <Badge className={cn('text-xs', policyStatusColor(status))}>{policyStatusLabel(status)}</Badge>
              <span className='text-sm text-muted-foreground'>
                {[policy?.policyCategory?.name ?? summary.policyCategory?.name, `${position} of ${total}`]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          </div>
          <div className='flex shrink-0 items-center gap-1'>
            {hasWrittenContent(policy) && (
              <AIButton
                label='Summarise'
                loading={aiSummary.isStreaming}
                onClick={handleSummarise}
                disabled={aiSummary.isStreaming}
              />
            )}
            <Button variant='link' size='sm' className='hidden h-8 md:inline-flex font-light' onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        <PolicyOwnerDetails owner={policy?.policyOwner} />
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6'>
        {isLoading && (
          <div className='mx-auto max-w-4xl space-y-3'>
            <Skeleton className='h-8 w-2/3' />
            <Skeleton className='h-3 w-full' />
            <Skeleton className='h-3 w-5/6' />
            <Skeleton className='h-48 w-full' />
          </div>
        )}
        {error && (
          <p className='py-10 text-center text-sm text-destructive'>
            {getErrorMessage(error, 'Failed to load policy. Please try again later.')}
          </p>
        )}
        {policy && isSummaryOpen && (
          <PolicyAiSummaryCard
            content={aiSummary.content}
            isStreaming={aiSummary.isStreaming}
            error={aiSummary.error}
            onClose={() => setIsSummaryOpen(false)}
          />
        )}
        {policy && <PolicyAcknowledgementContent policy={policy} />}
      </div>

      {policy && (
        <div className='flex-shrink-0 border-t bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-3'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-sm text-muted-foreground'>
              {isAcknowledged
                ? `Acknowledged${summary.acknowledgementDate ? ` ${formatDate(summary.acknowledgementDate)}` : ''}`
                : 'I have read this policy and acknowledge it'}
            </p>
            {isAcknowledged && nextPendingPolicy ? (
              <Button
                className='h-10 w-full sm:h-9 sm:w-auto'
                onClick={() => onSelectNext(nextPendingPolicy.policyHistoryId)}
              >
                Next policy
                <ChevronRight className='h-4 w-4' />
              </Button>
            ) : (
              !isAcknowledged && (
                <Button className='h-10 w-full sm:h-9 sm:w-auto' onClick={() => setOpenModal(true)}>
                  Acknowledge
                </Button>
              )
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={openModal}
        setOpen={setOpenModal}
        title='Confirm Policy Acknowledgement'
        description={`Confirm that you have read and understood the "${policyName}".`}
        buttonLabel='Confirm Acknowledgement'
        loadingButtonLabel='Submitting acknowledgement...'
        isNested={true}
        hideToast
        onSubmit={async () => {
          setConfirmLoading(true)
          await handleAcknowledgement()
          setConfirmLoading(false)
        }}
        loading={confirmLoading}
      />
    </section>
  )
}

const PoliciesAcknowledgementPage = () => {
  const { token } = useParams()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filter, setFilter] = useState<PolicyFilter>('all')
  const selectedPolicyHistoryId = searchParams.get(SELECTED_POLICY_PARAM)

  const listQueryKey = ['policies-bundle', token]
  const { data, isLoading, error } = useQuery({
    queryKey: listQueryKey,
    enabled: !!token,
    retry: false,
    queryFn: async () => {
      const response = await PublicAxiosInstance.get(`/policy/secure-bundle/${token}`)
      return response.data.data as IPoliciesBundleData
    },
  })

  if (!token) return <Navigate to={'/'} />

  if (isLoading) return <FullPageSkeleton />

  if (error || !data) return <AccessError statusCode={(error as ApiError)?.response?.status ?? null} />

  const { policies, staff } = data
  const acknowledgedCount = policies.filter((policy) => policy.isAcknowledged).length
  const isAllAcknowledged = policies.length > 0 && acknowledgedCount === policies.length
  const counts: Record<PolicyFilter, number> = {
    all: policies.length,
    pending: policies.length - acknowledgedCount,
    acknowledged: acknowledgedCount,
  }
  const visiblePolicies = policies.filter((policy) => {
    if (filter === 'pending') return !policy.isAcknowledged
    if (filter === 'acknowledged') return policy.isAcknowledged
    return true
  })
  const selectedIndex = policies.findIndex((policy) => policy.policyHistoryId === selectedPolicyHistoryId)
  const selectedPolicy = selectedIndex >= 0 ? policies[selectedIndex] : undefined
  const isSplit = !!selectedPolicy

  const selectPolicy = (policyHistoryId: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (policyHistoryId) next.set(SELECTED_POLICY_PARAM, policyHistoryId)
    else next.delete(SELECTED_POLICY_PARAM)
    setSearchParams(next, { replace: true })
  }

  const handleAcknowledged = (policyHistoryId: string) => {
    queryClient.setQueryData<IPoliciesBundleData>(listQueryKey, (current) =>
      current
        ? {
            ...current,
            policies: current.policies.map((policy) =>
              policy.policyHistoryId === policyHistoryId
                ? { ...policy, isAcknowledged: true, acknowledgementDate: new Date().toISOString() }
                : policy
            ),
          }
        : current
    )
    queryClient.invalidateQueries({ queryKey: listQueryKey })
  }

  const nextPendingPolicy = policies.find(
    (policy) => !policy.isAcknowledged && policy.policyHistoryId !== selectedPolicyHistoryId
  )

  const subtitle = [staff?.name ? `Shared with ${staff.name}` : null].filter(Boolean).join(' · ')

  return (
    <div className='flex h-dvh flex-col bg-background'>
      <header className={cn('flex-shrink-0 border-b px-4 py-3 sm:px-6', isSplit && 'hidden md:block')}>
        <div
          className={cn(
            'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
            !isSplit && 'mx-auto max-w-4xl'
          )}
        >
          <div className='min-w-0'>
            <h1 className='text-base font-semibold sm:text-lg'>Policies for acknowledgement</h1>
            {subtitle && <p className='truncate text-sm text-muted-foreground'>{subtitle}</p>}
          </div>
          <ProgressSummary acknowledged={acknowledgedCount} total={policies.length} />
        </div>
      </header>

      <main className='flex min-h-0 flex-1'>
        <aside
          className={cn(
            'flex min-h-0 min-w-0 flex-col',
            isSplit ? 'hidden border-r md:flex md:w-80 md:shrink-0 lg:w-96' : 'w-full'
          )}
          aria-label='Shared policies'
        >
          <div className={cn('flex min-h-0 flex-1 flex-col', isSplit ? 'px-5' : 'mx-auto w-full max-w-4xl')}>
            {policies.length > 0 && (
              <div className={cn('flex-shrink-0')}>
                <FilterTabs value={filter} counts={counts} onChange={setFilter} />
              </div>
            )}

            <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain'>
              {isAllAcknowledged && (
                <p className='border-b py-4 text-sm text-muted-foreground '>All policies acknowledged.</p>
              )}

              {policies.length === 0 || visiblePolicies.length === 0 ? (
                <div className='py-16 text-center'>
                  <p className='text-sm text-muted-foreground'>
                    {policies.length === 0 ? 'No policies are available in this link.' : 'No policies in this filter.'}
                  </p>
                  {policies.length > 0 && (
                    <Button variant='link' className='mt-1 h-auto p-0' onClick={() => setFilter('all')}>
                      Show all
                    </Button>
                  )}
                </div>
              ) : (
                visiblePolicies?.map((policy) => (
                  <PolicyListItem
                    key={policy.policyHistoryId}
                    policy={policy}
                    isSelected={policy.policyHistoryId === selectedPolicyHistoryId}
                    onSelect={() => selectPolicy(policy.policyHistoryId)}
                  />
                ))
              )}
            </div>
          </div>
        </aside>

        {selectedPolicy && (
          <div className='min-h-0 min-w-0 flex-1'>
            <PolicyDetailPanel
              key={selectedPolicy.policyHistoryId}
              token={token}
              summary={selectedPolicy}
              position={selectedIndex + 1}
              total={policies.length}
              nextPendingPolicy={nextPendingPolicy}
              onClose={() => selectPolicy(null)}
              onAcknowledged={handleAcknowledged}
              onSelectNext={selectPolicy}
            />
          </div>
        )}
      </main>
    </div>
  )
}

export default PoliciesAcknowledgementPage
